import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Input, Textarea } from '@/components/ui';
import { UserGroupService } from '@/services/userGroups.service';
import { useNotificationStore } from '@/store/notificationStore';
import { SimpleSwitch } from '@/components/ui/SimpleSwitch';
import type { GroupRole, UserGroup } from '@/types/userGroups.types';

interface UserGroupRightsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  groupRole: GroupRole;
  userGroups: UserGroup[];
  onSuccess: () => void;
}

interface UserGroupRoleFormData {
  userGroupId: number;
  isActive: boolean;
  allowedStartDate: string;
  allowedEndDate: string;
  description: string;
  hasChanged: boolean;
}

/**
 * UserGroupRightsDialog - Advanced editing dialog for role permissions across user groups
 * Allows setting date ranges, descriptions, and active status per group
 */
export const UserGroupRightsDialog: React.FC<UserGroupRightsDialogProps> = ({
  isOpen,
  onClose,
  groupRole,
  userGroups,
  onSuccess,
}) => {
  const { addNotification } = useNotificationStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<Record<number, UserGroupRoleFormData>>({});
  const [initialData, setInitialData] = useState<Record<number, UserGroupRoleFormData>>({});
  const [isRequiredRoles, setIsRequiredRoles] = useState<Record<number, boolean>>({});

  const loadRoleData = useCallback(async () => {
    setIsLoading(true);
    try {
      const existingRoles = await UserGroupService.getGroupRolesByCode(groupRole.roleCode);

      const newFormData: Record<number, UserGroupRoleFormData> = {};
      const newIsRequired: Record<number, boolean> = {};

      userGroups.forEach((group) => {
        const existingRole = existingRoles.find((role) => role.userGroupId === group.id);
        const isRequired = group.requiredRoleCodes?.includes(groupRole.roleCode) || false;

        newFormData[group.id] = {
          userGroupId: group.id,
          isActive: existingRole?.isActive ?? group.roleCodes.includes(groupRole.roleCode),
          allowedStartDate: existingRole?.allowedStartDate
            ? new Date(existingRole.allowedStartDate).toISOString().split('T')[0]
            : '',
          allowedEndDate: existingRole?.allowedEndDate
            ? new Date(existingRole.allowedEndDate).toISOString().split('T')[0]
            : '',
          description: existingRole?.description || existingRole?.roleDescription || '',
          hasChanged: false,
        };

        newIsRequired[group.id] = isRequired;
      });

      setFormData(newFormData);
      setInitialData(JSON.parse(JSON.stringify(newFormData)));
      setIsRequiredRoles(newIsRequired);

      // Expand first group by default
      if (userGroups.length > 0) {
        setExpandedGroups(new Set([userGroups[0].id]));
      }
    } catch (error) {
      console.error('Failed to load role data:', error);
      addNotification({
        type: 'error',
        title: 'Load Failed',
        message: 'Failed to load role data. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupRole, userGroups, addNotification]);

  // Load existing role data when dialog opens
  useEffect(() => {
    if (isOpen && groupRole && userGroups.length > 0) {
      loadRoleData();
    }
  }, [isOpen, groupRole, userGroups, loadRoleData]);

  const toggleGroup = (groupId: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleFieldChange = (
    groupId: number,
    field: keyof UserGroupRoleFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => {
      const updated = { ...prev };
      updated[groupId] = { ...updated[groupId], [field]: value };

      // Check if data has changed
      const initial = initialData[groupId];
      const current = updated[groupId];
      updated[groupId].hasChanged =
        current.isActive !== initial.isActive ||
        current.allowedStartDate !== initial.allowedStartDate ||
        current.allowedEndDate !== initial.allowedEndDate ||
        current.description !== initial.description;

      return updated;
    });
  };

  const getChangedRoles = (): UserGroupRoleFormData[] => {
    return Object.values(formData).filter((data) => data.hasChanged);
  };

  const handleSave = async () => {
    const changedRoles = getChangedRoles();

    if (changedRoles.length === 0) {
      addNotification({
        type: 'info',
        title: 'No Changes',
        message: 'No changes have been made.',
      });
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      // Update all changed roles in parallel
      await Promise.all(
        changedRoles.map((roleData) =>
          UserGroupService.updateGroupRoles(roleData.userGroupId, {
            roleCode: groupRole.roleCode,
            isActive: roleData.isActive,
            allowedStartDate: roleData.allowedStartDate || null,
            allowedEndDate: roleData.allowedEndDate || null,
            description: roleData.description,
          })
        )
      );

      onSuccess();

      // Small delay to ensure notification is visible before closing
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Rights: ${groupRole.roleName}`.toUpperCase()}
      size="lg"
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <svg className="h-8 w-8 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-25"
              />
              <path
                fill="currentColor"
                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                className="opacity-75"
              />
            </svg>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Loading user groups, please wait...
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {userGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.id);
              const groupData = formData[group.id];
              const isRequired = isRequiredRoles[group.id];

              return (
                <div
                  key={group.id}
                  className="overflow-hidden rounded-lg border border-gray-200 dark:border-dark-600"
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100 dark:bg-dark-800 dark:hover:bg-dark-700"
                  >
                    <div className="flex-1">
                      <div className="text-base font-semibold text-gray-900 dark:text-white">
                        {group.name}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Last Updated: {formatDate(group.updatedAt)} - {group.updateByUser}
                      </div>
                    </div>
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform dark:text-gray-400 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-white p-4 dark:border-dark-600 dark:bg-dark-900">
                      <div className="space-y-4">
                        {/* Description */}
                        <Textarea
                          label="Description"
                          name={`description-${group.id}`}
                          value={groupData.description}
                          onChange={(e) =>
                            handleFieldChange(group.id, 'description', e.target.value)
                          }
                          rows={3}
                          placeholder="Add notes or description for this role assignment..."
                        />

                        {/* Date Range */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <Input
                            label="Allow Start Date"
                            name={`startDate-${group.id}`}
                            type="date"
                            value={groupData.allowedStartDate}
                            onChange={(e) =>
                              handleFieldChange(group.id, 'allowedStartDate', e.target.value)
                            }
                            max={groupData.allowedEndDate || undefined}
                          />
                          <Input
                            label="Allow End Date"
                            name={`endDate-${group.id}`}
                            type="date"
                            value={groupData.allowedEndDate}
                            onChange={(e) =>
                              handleFieldChange(group.id, 'allowedEndDate', e.target.value)
                            }
                            min={groupData.allowedStartDate || undefined}
                          />
                        </div>

                        {/* Is Active Switch */}
                        <div className="flex items-center justify-between">
                          <SimpleSwitch
                            checked={groupData.isActive}
                            onChange={(checked) => handleFieldChange(group.id, 'isActive', checked)}
                            label="Is Active?"
                            disabled={isRequired}
                          />
                          {isRequired && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Required for {group.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            loading={isSaving}
            rightIcon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            }
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};
